import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type Method,
} from "axios";
import type { QueryClient } from "@tanstack/react-query";
import { z } from "zod";

export const DEFAULT_HTTP_STATUS_CODES = {
  UNAUTHORIZED: 401,
  NOT_FOUND: 404,
} as const;

type QueryKeyInput = string | readonly unknown[];

type AuthTokens = {
  accessToken?: string;
  refreshToken?: string;
};

type ApiRequestConfig = AxiosRequestConfig & {
  skipErrorNotification?: boolean;
};

type ApiServiceOptions = {
  baseURL?: string;
  queryClient?: QueryClient;
  getAccessToken?: () => string | null | undefined;
  getRefreshToken?: () => string | null | undefined;
  saveAuthTokens?: (tokens: AuthTokens) => void;
  clearAuth?: (error?: unknown) => void;
  refreshUrl?: string;
  refreshRequest?: (refreshToken: string) => Promise<AxiosResponse | unknown>;
  getRefreshPayload?: (refreshToken: string) => unknown;
  extractRefreshTokens?: (data: any) => AuthTokens;
  onUnauthorized?: (error?: unknown) => void;
  notifyError?: (error: AxiosError<any>) => void;
  invalidCredentialsErrorCode?: string | number;
  refreshInvalidationQueryKeys?: QueryKeyInput[];
  httpStatusCodes?: typeof DEFAULT_HTTP_STATUS_CODES;
  axiosInstance?: AxiosInstance;
};

const normalizeQueryKey = (queryKey: QueryKeyInput) =>
  typeof queryKey === "string" ? [queryKey] : queryKey;

const isAxiosResponseLike = (value: unknown): value is AxiosResponse =>
  Boolean(value && typeof value === "object" && "data" in value);

export const authTokensSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().optional().nullable(),
}).transform((tokens) => ({
  accessToken: tokens.accessToken,
  refreshToken: tokens.refreshToken ?? undefined,
}));

const refreshResponseSchema = z
  .object({
    response: z.object({
      access_token: z.string().min(1),
      refresh_token: z.string().optional().nullable(),
    }),
  })
  .transform(({ response }) => ({
    accessToken: response.access_token,
    refreshToken: response.refresh_token ?? undefined,
  }));

const setAuthorizationHeader = (config: AxiosRequestConfig, token: string) => {
  config.headers = config.headers || {};
  (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
};

export const createApiService = ({
  baseURL,
  queryClient,
  getAccessToken,
  getRefreshToken,
  saveAuthTokens,
  clearAuth,
  refreshUrl,
  refreshRequest,
  getRefreshPayload = (refreshToken) => ({ refreshToken }),
  extractRefreshTokens = (data) => refreshResponseSchema.parse(data),
  onUnauthorized,
  notifyError,
  invalidCredentialsErrorCode,
  refreshInvalidationQueryKeys = [],
  httpStatusCodes = DEFAULT_HTTP_STATUS_CODES,
  axiosInstance,
}: ApiServiceOptions = {}) => {
  const apiService =
    axiosInstance ||
    axios.create({
      baseURL,
    });

  const invalidateConfiguredQueries = (queryKeys: QueryKeyInput[] = []) => {
    queryKeys.forEach((queryKey) => {
      queryClient?.invalidateQueries?.({ queryKey: normalizeQueryKey(queryKey) });
    });
  };

  apiService.interceptors.request.use(
    (config) => {
      const token = getAccessToken?.();
      if (token) {
        setAuthorizationHeader(config, token);
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  const handleUnauthorizedError = async () => {
    try {
      const refreshToken = getRefreshToken?.();
      if (!refreshToken) {
        onUnauthorized?.();
        return false;
      }

      if (!refreshRequest && !refreshUrl) {
        onUnauthorized?.();
        return false;
      }

      const refreshResponse =
        typeof refreshRequest === "function"
          ? await refreshRequest(refreshToken)
          : await axios.post(refreshUrl, getRefreshPayload(refreshToken), {
              headers: {
                "Content-Type": "application/json",
              },
            });

      const refreshData = axios.isAxiosError(refreshResponse)
        ? refreshResponse.response?.data
        : isAxiosResponseLike(refreshResponse)
          ? refreshResponse.data
          : refreshResponse;
      const tokens = authTokensSchema.parse(extractRefreshTokens(refreshData));
      saveAuthTokens?.(tokens);

      queryClient?.invalidateQueries?.();
      invalidateConfiguredQueries(refreshInvalidationQueryKeys);
      return true;
    } catch (error) {
      clearAuth?.(error);
      onUnauthorized?.(error);
      return false;
    }
  };

  const request = async (
    method: Method,
    url: string,
    data: unknown = {},
    config: ApiRequestConfig = {},
    isRetry = false,
    isReturnAll = false,
  ) => {
    try {
      const response = await apiService({ method, url, data, ...config });
      return isReturnAll ? response : response.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const status = axiosError.response?.status;
      const errorCode = axiosError.response?.data?.errorCode;
      const isUnauthorized =
        status === httpStatusCodes.UNAUTHORIZED &&
        errorCode !== invalidCredentialsErrorCode;

      if (isUnauthorized && !isRetry) {
        const isRefreshSuccessful = await handleUnauthorizedError();
        if (!isRefreshSuccessful) {
          throw error;
        }

        const token = getAccessToken?.();
        if (!token) {
          onUnauthorized?.();
          throw error;
        }

        setAuthorizationHeader(config, token);
        return request(method, url, data, config, true, isReturnAll);
      }

      if (!config?.skipErrorNotification && status !== httpStatusCodes.NOT_FOUND) {
        notifyError?.(axiosError);
      }

      throw error;
    }
  };

  return {
    axiosInstance: apiService,
    request,
    get: (url: string, config: ApiRequestConfig = {}, isReturnAll = false) =>
      request("get", url, {}, config, false, isReturnAll),
    post: (url: string, data?: unknown, config: ApiRequestConfig = {}) =>
      request("post", url, data, config),
    put: (url: string, data?: unknown, config: ApiRequestConfig = {}) =>
      request("put", url, data, config),
    patch: (url: string, data?: unknown, config: ApiRequestConfig = {}) =>
      request("patch", url, data, config),
    delete: (url: string, config: ApiRequestConfig = {}) =>
      request("delete", url, {}, config),
  };
};
